import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ModalWrapper } from '../../shared/components';
import { QuotationPdfFile } from '../../admin/admin.api';

/**
 * The quotation's offer letter, shown on the page rather than in another tab.
 *
 * The letter is read against the quotation it came from, so sending it to a separate tab
 * lost the row it belonged to. It renders in the browser's own PDF viewer through an
 * iframe on the blob URL, which keeps the viewer's page controls, zoom, print and save
 * without this screen reimplementing any of them.
 *
 * The URL belongs to the caller: this component only displays it, and never revokes it.
 * Closing goes through `onClose` so the owner can revoke at the moment it chooses.
 */
export default function QuotationPdfViewer({
  file,
  title,
  onClose,
}: {
  /** null keeps the viewer closed. */
  file: QuotationPdfFile | null;
  /** Names the quotation being read, so the letter stays tied to its row. */
  title: string;
  onClose: () => void;
}) {
  return (
    <ModalWrapper
      open={file !== null}
      onClose={onClose}
      isFullWidth
      sx={{ p: 2, gap: 1.5, height: '90vh' }}
    >
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
        <Typography variant='body1' color='text.primary' sx={{ minWidth: 0 }} noWrap>
          {title}
        </Typography>

        <Stack direction='row' alignItems='center' gap={1}>
          {/*
            A real download alongside the viewer's own: the browser's built-in save button
            is not where everyone looks for it, and this one uses the filename the server
            chose rather than the blob URL's uuid.
          */}
          <Button
            size='small'
            variant='outlined'
            component='a'
            href={file?.url}
            download={file?.filename}
            startIcon={<Icon icon='mdi:download' width={18} />}
          >
            Download
          </Button>
          <Tooltip title='Close'>
            <IconButton size='small' onClick={onClose} aria-label='Close quotation PDF'>
              <Icon icon='mdi:close' width={20} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/*
        Keyed on the URL so opening a second quotation replaces the document rather than
        leaving the iframe showing the first one.
      */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {file && (
          <iframe
            key={file.url}
            src={file.url}
            title={title}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        )}
      </Box>
    </ModalWrapper>
  );
}
